import {
  Button, Container, Pagination, StatusMessage, TextualLink, Table, Tooltip,
} from '@hvcgroep/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import useContract from '../../hooks/useContract';
import InvoiceTable from '../../components/InvoiceTable';
import usePaginatedInvoices from '../../hooks/usePaginatedInvoices';
import ContractSwitcherHeader from '../../containers/ContractSwitcherHeader';
import formatCurrency from '../../helpers/formatCurrency';
import SkeletonLoader from '../../components/SkeletonLoader';
import ErrorPage from '../ErrorPage';

import './style.scss';
import CustomButtonLinkWrapper from '../../components/CustomButtonLinkWrapper';

const Invoices: React.FC = () => {
  const { t } = useTranslation();
  const textId = 'text-change-installment-amount';
  const heading = t('pages.invoices.title');
  const siteTitle = t('general.siteTitle');

  const {
    canAdjustAdvancePayment,
    selectedAccount,
    selectedContract,
    isContractDatePassed,
    isLoading: isContractLoading,
    isError: isContractError,
  } = useContract();

  const status = selectedContract?.status;
  const isEditable = status !== 'FINALIZED' && status !== 'TERMINATED';

  const {
    invoices,
    pagination,
    isLoading: isPaginatedInvoicesLoading,
    isError: isPaginatedInvoicesError,
  } = usePaginatedInvoices(selectedAccount?.id);

  const isLoading = isContractLoading || isPaginatedInvoicesLoading;

  if (isContractError) {
    return <ErrorPage type="serverError" />;
  }

  return (
    <Container fluid>
      <Helmet><title>{`${heading} - ${siteTitle}`}</title></Helmet>

      {(invoices.length === 0 && isLoading) && (<SkeletonLoader type="invoices" />)}

      {!isLoading && (
        <>
          <ContractSwitcherHeader
            heading={heading}
            {...(invoices.length ? {
              subheading: `(${t('pages.invoices.invoiceCount', { count: pagination.totalInvoices })})`,
            } : {})}
          />
          <div className="o-background--grey mo-advance-payment-card">
            {!isContractDatePassed && selectedAccount?.advancePayment != null && (
            <div>
              <p className="text-large u-margin--bottom--0">
                {t('advancePayment.currentAmount')}:&nbsp;
                <span className="text-bold">
                  {formatCurrency(selectedAccount.advancePayment)}
                </span>
              </p>
              <p className="mo-advance-payment-card__payment-details">
                <span>
                  {t('advancePayment.iban')}: {selectedAccount.financial?.bankAccountNumber}&nbsp;
                </span>
                {isEditable ? (
                  <span>
                    (
                    <TextualLink asChild className="font-weight-normal">
                      <Link to="/betaalgegevens-wijzigen">{t('advancePayment.amendPaymentDetails')}</Link>
                    </TextualLink>
                    )
                  </span>
                ) : (
                  <div className="d-flex">
                    <p className="text-bold text-muted">({t('advancePayment.amendPaymentDetails')})</p>
                    <Tooltip title={t('pages.changePaymentDetails.notEditable.paymentDetails')} tooltipId="tooltip_desc" />
                  </div>
                )}
              </p>
            </div>
            )}
            {!isContractDatePassed && (
              canAdjustAdvancePayment ? (
                <div>
                  <Button href="/termijnbedrag-wijzigen" variation="center" CustomLinkComponent={CustomButtonLinkWrapper}>
                    {t('advancePayment.adjustAdvancePayment')}
                  </Button>
                </div>
              ) : (
                <div className="mo-cant-adjust-payment">
                  <Button variation="center" disabled aria-describedby={textId}>
                    {t('advancePayment.adjustAdvancePayment')}
                  </Button>
                  <p id={textId} className="text-muted">{t('pages.invoices.changeInstallmentAmountTwice')}</p>
                </div>
              )
            )}
            { isContractDatePassed && (
              <>
                <p>
                  {t('pages.overview.contractDatePassed')}
                </p>
              </>
            )}
          </div>
          <InvoiceTable
            invoices={invoices}
            className="u-margin--bottom--4"
          />
          {(invoices.length === 0 && !isPaginatedInvoicesError) && (
            <StatusMessage type="info">
              {t('invoiceTable.noInvoicesFound')}
            </StatusMessage>
          )}

          {isPaginatedInvoicesError && (
            <StatusMessage type="error">
              {t('invoiceTable.invoicesError')}
            </StatusMessage>
          )}

          {(pagination.totalPages > 1 && !isPaginatedInvoicesError) && (
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageClick={(page) => pagination.setCurrentPage(page)}
            />
          )}

        </>
      )}
    </Container>
  );
};

export default Invoices;
